/**
 * Read-model extensions for Financial Snapshot until AIM ships them on
 * `@brightside/financial-snapshot-types` (DEP-3). Re-export shared base types.
 */

export type {
  FinancialSnapshotData,
  FinancialSnapshotTemplate,
  SnapshotCategory,
  SnapshotCategoryWithData,
  SnapshotSubCategoryWithData,
  BreakdownCategoryData,
  CategoryBase,
} from '@brightside/financial-snapshot-types';

/** Shared package category data (required `value` on subs) — re-exported under alias. */
export type { SnapshotCategoryData as SharedSnapshotCategoryData } from '@brightside/financial-snapshot-types';

export { MacroCategory, FinancialSnapshotTransformUtils } from '@brightside/financial-snapshot-types';

/** API-computed document status. UI renders verbatim. */
export type SnapshotStatus = 'empty' | 'in_progress' | 'complete' | 'outdated';

export type NetCashflowStatus = 'surplus' | 'shortfall' | 'break_even';

export type SnapshotEditor = 'fa' | 'client';

/** Document-level derived metrics (API-computed; UI renders as-is). */
export interface SnapshotMetrics {
  householdIncome: number | null;
  monthlyCosts: number | null;
  netCashflow: number | null;
  netCashflowStatus: NetCashflowStatus | null;
  costsVsIncomePercent: number | null;
}

/**
 * Per-subcategory value + audit.
 * `value` undefined = blank (not entered); `0` = explicitly entered.
 * Never coerce blank → 0.
 */
export interface SnapshotSubCategoryData {
  value?: number;
  label?: string;
  lastEditedAt?: string;
  lastEditedBy?: SnapshotEditor;
  lastEditedById?: string;
}

/**
 * Per-category read model with optional percentOfIncome + audit.
 * Declared standalone (does not extend shared SnapshotCategoryData) so blank≠0
 * can use optional `value` until DEP-3 updates the shared package.
 */
export interface SnapshotCategoryData {
  percentOfIncome?: number | null;
  totalValue?: number;
  lastEditedAt?: string;
  lastEditedBy?: SnapshotEditor;
  lastEditedById?: string;
  subCategories?: Record<string, SnapshotSubCategoryData>;
}

/** GET /financialsnapshot response (200). */
export interface FinancialSnapshotResponse {
  guid: string;
  createdDate?: string;
  modifiedDate?: string;
  submittedDate?: string;
  lastUpdatedAt?: string;
  status: SnapshotStatus;
  categories: Record<string, SnapshotCategoryData>;
  metrics: SnapshotMetrics;
}

/** Result of GET: 204 empty vs 200 payload. */
export type FinancialSnapshotLoadResult =
  | { kind: 'empty' }
  | { kind: 'snapshot'; snapshot: FinancialSnapshotResponse };

/** POST body (full upsert). */
export interface FinancialSnapshotPostBody {
  guid: string;
  categories: Record<string, SnapshotCategoryData>;
  createdDate?: string;
  submittedDate?: string;
  lastEditedBy?: SnapshotEditor;
  lastEditedById?: string;
}

/** PATCH body (single category). */
export type FinancialSnapshotPatchBody = Record<string, SnapshotCategoryData>;

/** Stored snapshot shape used by the mock (may omit status/metrics until recomputed). */
export type FinancialSnapshotDocument = Omit<FinancialSnapshotResponse, 'status' | 'metrics'> & {
  status?: SnapshotStatus;
  metrics?: SnapshotMetrics;
};
